import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  MapPin, 
  Calendar, 
  User, 
  Search, 
  Navigation, 
  CheckCircle,
  ArrowRight,
  Loader,
  X
} from 'lucide-react'
import { useUserStore } from '../stores/userStore'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

interface InteractiveBirthFormProps {
  onComplete?: (birthData: any) => void
  onCancel?: () => void
}

const InteractiveBirthForm: React.FC<InteractiveBirthFormProps> = ({ 
  onComplete, 
  onCancel 
}) => {
  const { updateUser } = useUserStore()
  const { user } = useAuth()
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [selectedLocation, setSelectedLocation] = useState<any>(null)
  
  const [formData, setFormData] = useState<Partial<any>>({
    name: '',
    place_of_birth: '',
    latitude: '',
    longitude: '',
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    minute: 0
  })

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Load existing user data when component mounts
  useEffect(() => {
    if (user?.birthData) {
      setFormData({
        name: user.birthData.name || '',
        place_of_birth: user.birthData.place_of_birth || '',
        latitude: user.birthData.latitude || '',
        longitude: user.birthData.longitude || '',
        day: user.birthData.day || 1,
        month: user.birthData.month || 1,
        year: user.birthData.year || 1990,
        hour: user.birthData.hour || 12,
        minute: user.birthData.minute || 0
      })
      
      // Set search query to existing location if available
      if (user.birthData.place_of_birth) {
        setSearchQuery(user.birthData.place_of_birth)
      }
    }
  }, [user])

  // Fetch user birth data from backend if not available in store
  const fetchUserBirthData = async () => {
    if (!user?.uid) return
    
    try {
      const response = await fetch(`/api/hybrid-rag/user-profile/${user.uid}`)
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.profile?.birthData) {
          const birthData = data.profile.birthData
          setFormData({
            name: birthData.name || '',
            place_of_birth: birthData.place_of_birth || '',
            latitude: birthData.latitude || '',
            longitude: birthData.longitude || '',
            day: birthData.day || 1,
            month: birthData.month || 1,
            year: birthData.year || 1990,
            hour: birthData.hour || 12,
            minute: birthData.minute || 0
          })
          
          // Set search query to existing location if available
          if (birthData.place_of_birth) {
            setSearchQuery(birthData.place_of_birth)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user birth data:', error)
    }
  }

  // Fetch user data from backend if not available in store
  useEffect(() => {
    if (!user?.birthData) {
      fetchUserBirthData()
    }
  }, [user?.uid])

  // Step titles
  const steps = [
    { id: 1, title: 'Your Name', icon: User },
    { id: 2, title: 'Birth Location', icon: MapPin },
    { id: 3, title: 'Birth Date & Time', icon: Calendar }
  ]

  // Location search with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchQuery.length > 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchLocations(searchQuery)
      }, 500)
    } else {
      setSearchResults([])
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  const searchLocations = async (query: string) => {
    try {
      setIsLoading(true)
      // Using OpenStreetMap Nominatim API for location search
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
      )
      const data = await response.json()
      setSearchResults(data)
    } catch (error) {
      console.error('Error searching locations:', error)
      toast.error('Failed to search locations')
    } finally {
      setIsLoading(false)
    }
  }

  const selectLocation = (location: any) => {
    setSelectedLocation(location)
    setFormData(prev => ({
      ...prev,
      place_of_birth: location.display_name,
      latitude: location.lat,
      longitude: location.lon
    }))
    setSearchQuery(location.display_name)
    setSearchResults([])
  }

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          // Reverse geocode to get location name
          fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          )
            .then(response => response.json())
            .then(data => {
              selectLocation({
                display_name: data.display_name,
                lat: latitude.toString(),
                lon: longitude.toString()
              })
              toast.success('Location detected!')
            })
            .catch(() => {
              toast.error('Could not get location name')
            })
            .finally(() => setIsLoading(false))
        },
        () => {
          toast.error('Could not get your location')
          setIsLoading(false)
        }
      )
    } else {
      toast.error('Geolocation not supported')
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const nextStep = () => {
    if (step < 3) {
      setStep(step + 1)
    }
  }

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const handleSubmit = async () => {
    if (!formData.name || !formData.place_of_birth) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      setIsLoading(true)
      const birthData: any = {
        name: formData.name!,
        place_of_birth: formData.place_of_birth!,
        latitude: formData.latitude || '',
        longitude: formData.longitude || '',
        day: formData.day!,
        month: formData.month!,
        year: formData.year!,
        hour: formData.hour!,
        minute: formData.minute!
      }

      await updateUser({ birthData })
      toast.success('Birth details saved successfully!')
      onComplete?.(birthData)
    } catch (error) {
      console.error('Error saving birth data:', error)
      toast.error('Failed to save birth details')
    } finally {
      setIsLoading(false)
    }
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="w-full max-w-md bg-gradient-to-br from-slate-900 via-purple-900/20 to-pink-900/20 rounded-3xl border border-white/10 backdrop-blur-xl p-6 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Birth Details</h2>
          <button
            onClick={onCancel}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((stepItem) => {
            const Icon = stepItem.icon
            const isActive = step === stepItem.id
            const isCompleted = step > stepItem.id
            
            return (
              <div key={stepItem.id} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isCompleted 
                    ? 'bg-green-500' 
                    : isActive 
                      ? 'bg-purple-500' 
                      : 'bg-white/20'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className="w-5 h-5 text-white" />
                  )}
                </div>
                <span className={`text-xs mt-2 ${
                  isActive ? 'text-purple-400' : 'text-gray-400'
                }`}>
                  {stepItem.title}
                </span>
              </div>
            )
          })}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Name */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div>
                <label className="block text-white font-medium mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>
              
              <button
                onClick={nextStep}
                disabled={!formData.name?.trim()}
                className="w-full mobile-button-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div>
                <label className="block text-white font-medium mb-2">Birth Location</label>
                
                {/* Search Input */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for your birth place..."
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  {isLoading && (
                    <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400 animate-spin" />
                  )}
                </div>

                {/* Current Location Button */}
                <button
                  onClick={getCurrentLocation}
                  disabled={isLoading}
                  className="w-full mb-4 mobile-button-secondary flex items-center justify-center space-x-2"
                >
                  <Navigation className="w-4 h-4" />
                  <span>Use Current Location</span>
                </button>

                {/* Search Results */}
                <AnimatePresence>
                  {searchResults.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="max-h-48 overflow-y-auto space-y-2"
                    >
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          onClick={() => selectLocation(result)}
                          className="w-full p-3 text-left bg-white/5 hover:bg-white/10 rounded-xl transition-colors border border-white/10"
                        >
                          <div className="flex items-start space-x-3">
                            <MapPin className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="text-white font-medium text-sm">
                                {result.display_name.split(',')[0]}
                              </div>
                              <div className="text-gray-400 text-xs">
                                {result.display_name}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Selected Location */}
                {selectedLocation && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <div className="text-white font-medium">Location Selected</div>
                        <div className="text-gray-300 text-sm">{selectedLocation.display_name}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={prevStep}
                  className="flex-1 mobile-button-secondary"
                >
                  Back
                </button>
                <button
                  onClick={nextStep}
                  disabled={!selectedLocation}
                  className="flex-1 mobile-button-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Date & Time */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div>
                <label className="block text-white font-medium mb-2">Birth Date</label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.day}
                      onChange={(e) => handleInputChange('day', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Month</label>
                    <select
                      value={formData.month}
                      onChange={(e) => handleInputChange('month', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    >
                      {months.map((month, index) => (
                        <option key={index + 1} value={index + 1} className="bg-slate-800">
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Year</label>
                    <input
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      value={formData.year}
                      onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Birth Time (24-hour format)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Hour</label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={formData.hour}
                      onChange={(e) => handleInputChange('hour', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Minute</label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={formData.minute}
                      onChange={(e) => handleInputChange('minute', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={prevStep}
                  className="flex-1 mobile-button-secondary"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 mobile-button-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <span>Save Details</span>
                      <CheckCircle className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

export default InteractiveBirthForm 